/**
 * Accounts and authentication (design D9 "Auth"): platform-neutral bearer credentials only, never cookies.
 *
 * Credentials
 *  - Guest accounts are bound to a device token (issued once by auth.guest, kept by the client).
 *  - Optional username + password credentials (behind PasswordHasher).
 *  - Every sign-in opens a DEVICE SESSION (sessionId, DeviceInfo, created / last seen) with a token pair: a short-lived
 *    access token (config.auth.accessTtlMs) that authorizes requests, and a long-lived refresh token
 *    (config.auth.refreshTtlMs) that auth.refresh exchanges for a new pair. The refresh token is ROTATED on every use;
 *    presenting a rotated one again revokes the whole session (theft detection), except the directly previous one:
 *    - a retry of the refresh whose reply was lost (the SAME request id; the session records it, so this holds after
 *      a restart too) within config.auth.refreshRetryWindowMs gets a valid pair: the same one while this process
 *      remembers it, else a new pair of the same generation that replaces the lost one;
 *    - under another request id within config.auth.refreshReuseGraceMs it gets the same new pair again while this
 *      process remembers it (another window of the same device refreshing at the same moment), else
 *      'refresh_rotated'.
 *  - One active session per account (config.auth.singleSession, the default): a sign-in (any method, a guest's device
 *    token too), or an auth.resume / auth.refresh of another session of the account, ends every other session of it
 *    (reason 'replaced', in one write under the account's auth mutex, so of two simultaneous sign-ins the later one
 *    wins). The ended sessions are remembered for a while (AccountSessionsRecord.replaced): their tokens answer
 *    UNAUTHORIZED 'session_replaced' instead of 'revoked' (the core closes their WebSockets with code 4004), and a log
 *    line 'sessions replaced' records who replaced whom. A resume or refresh of the SAME session (the same device back
 *    from the background) replaces nothing. With singleSession off an account keeps at most
 *    config.auth.maxSessionsPerAccount sessions; one more sign-in ends the least recently used one.
 *
 * At rest the server keeps only keyed hashes of tokens: HMAC-SHA256(AUTH_SECRET, token) (TokenDigest), or plain SHA-256
 * when the backend has no secret (the in-process backend; the self-hosted server warns at startup). Previous secrets
 * (secret rotation) are still accepted for lookups. Device tokens of an earlier backend (SHA-256) are re-keyed on use;
 * a session token of the protocol 1 backend is converted into a device session once (auth.resume).
 *
 * Nickname accounts (the game's accounts: auth.signUp / auth.signIn; rules in src/shared/auth-rules.ts)
 *  - The account keeps the display nickname (case kept) and is indexed in C.usernames under the nickname KEY
 *    (NFC, trimmed, lower case), the same index as the username accounts of auth.register: one namespace, so a nickname
 *    and a username never collide case-insensitively. Guest default names (`<prefix>-XXXX`) are reserved, and so are
 *    the display names that accounts without a nickname chose (C.displayNames: auth.guest / auth.register displayName,
 *    profile.setDisplayName; the same rules as a nickname). A name also counts as taken when its whole-script lookalike
 *    is (src/shared/auth-rules.ts nicknameLookalikes). Every check and claim of a name runs under the lock of its
 *    skeleton (`username:<skeleton>`, withNameLocks), so lookalike names are never claimed at the same time.
 *  - The display name of a nickname account is its nickname: profile.setDisplayName only changes its case there.
 *  - The password is hashed by the PasswordHasher (scrypt on the server, PBKDF2 in-process); only the hash is stored.
 *  - auth.signIn answers an unknown nickname and a wrong password the same way ('invalid_credentials') and hashes the
 *    given password with a fresh salt for an unknown nickname, so both take about one hash of time. Failed sign-ins
 *    are counted per nickname key, whether an account has it or not, and per client address (IPv6: its /64), in a
 *    sliding window (config.auth.maxLoginFailures, maxLoginFailuresPerAddress; RATE_LIMITED 'too_many_attempts' with
 *    details.scope). auth.login counts the same way ('too_many_login_failures'). The check of the limits and the
 *    reservation of the attempt are one synchronous step before the first await (../failure-counter.ts), so a burst
 *    of parallel sign-ins gets no more password checks than the limit; the per-address request limits of the router
 *    apply as well. Nicknames are public, so anybody could keep a player's nickname locked with a few wrong passwords
 *    every window: an address that signed in to the name with its right password lately (KNOWN_ADDRESSES_PER_NAME per
 *    name, memory only) is not held by the name's lock but by a limit of its own for the name and that address (the
 *    same maxLoginFailures; its failures count on the name as well).
 *  - The password is never logged, never stored in plain text and never part of an error.
 *
 * Ended sessions (logout, auth.revoke, eviction, replacement, reuse) are reported to the listener registered with
 * `onSessionsEnded` (the core closes their connections). No VK ids, cookies or web sessions are involved.
 */
import {
  type CredentialProblem,
  type NicknameProblem,
  checkNickname,
  checkPassword,
  checkSignIn,
  checkSignUp,
  isGuestDefaultName,
  nicknameKey,
  nicknameLookalikes,
  nicknameSkeleton,
  normalizeNickname,
} from '../../../shared/auth-rules';
import { HmacSha256 } from '../../../shared/crypto/hmac';
import { sha256Hex } from '../../../shared/crypto/sha256';
import { randomId, randomInt, randomToken } from '../../../shared/random';
import { BackendError } from '../../../shared/protocol/errors';
import type {
  AccountInfo,
  AuthFailureReason,
  AuthResult,
  AuthSessionInfo,
  BanInfo,
  CurrentSessionInfo,
  DeviceInfo,
  IssuedAuthResult,
  PlayerId,
} from '../../../shared/protocol/model';
import { addressLimitKey } from '../client-address';
import { FailureCounter } from '../failure-counter';
import type { AuthState } from '../hub';
import type { PasswordHasher } from '../password';
import {
  type AccountRecord,
  type AccountSessionsRecord,
  type AuthSessionRecord,
  type AuthTokenRecord,
  type DeviceRecord,
  type ReplacedSessionRecord,
  type DisplayNameRecord,
  type ServiceDeps,
  type TokenRecord,
  type UsernameRecord,
  C,
} from './deps';

/** Keyed at-rest hashes of tokens: HMAC-SHA256(secret, token), or plain SHA-256 without a secret. */
export class TokenDigest {
  private readonly current: HmacSha256 | null;
  private readonly previous: HmacSha256[];

  constructor(secret?: string | null, previousSecrets: readonly string[] = []) {
    this.current = secret ? new HmacSha256(secret) : null;
    this.previous = previousSecrets.filter((s) => s && s !== secret).map((s) => new HmacSha256(s));
  }

  /** True when tokens are hashed with a server secret (HMAC). */
  get keyed(): boolean {
    return this.current !== null;
  }

  /** The hash new records are stored under. */
  hash(token: string): string {
    return this.current ? this.current.hex(token) : sha256Hex(token);
  }

  /** Hashes under the previous secrets (records written before a secret rotation). */
  previousHashes(token: string): string[] {
    return this.previous.map((k) => k.hex(token));
  }

  /** Every other hash a record of this token may be stored under: previous secrets, then plain SHA-256. */
  fallbackHashes(token: string): string[] {
    const out = this.previousHashes(token);
    if (this.current) out.push(sha256Hex(token));
    return out;
  }

  /**
   * Keys derived from the secrets for other protections at rest (HMAC(secret, label); the operator console seals its
   * TOTP secrets with them): the current secret's key first, then the previous secrets' keys. Empty without a secret.
   * The secrets themselves never leave this object.
   */
  derivedKeys(label: string): Uint8Array[] {
    if (!this.current) return [];
    return [this.current.digest(label), ...this.previous.map((k) => k.digest(label))];
  }
}

/** UNAUTHORIZED with a stable `details.reason`. */
export function authFailure(reason: AuthFailureReason, message = AUTH_FAILURE_MESSAGES[reason]): BackendError {
  return new BackendError('UNAUTHORIZED', message, { reason });
}

export const AUTH_FAILURE_MESSAGES: { readonly [K in AuthFailureReason]: string } = {
  session_required: 'Authentication required',
  invalid_token: 'The token is invalid',
  token_expired: 'The access token has expired; refresh it (auth.refresh)',
  refresh_expired: 'The refresh token has expired; sign in again',
  revoked: 'The session has been ended',
  refresh_reused: 'This refresh token was already used: the session is revoked',
  refresh_rotated: 'This refresh token was just rotated; use the newer one',
  bad_credentials: 'Invalid username or password',
  invalid_credentials: 'Invalid nickname or password',
  session_replaced: 'This account signed in on another device: this session has been ended',
};

/** English messages of the credential problems (clients show their own text by `details.reason`). */
const CREDENTIAL_PROBLEM_MESSAGES: { readonly [K in CredentialProblem['reason']]: string } = {
  nickname_required: 'Enter a nickname',
  nickname_too_short: 'The nickname is too short',
  nickname_too_long: 'The nickname is too long',
  nickname_invalid: 'The nickname may contain only Latin or Cyrillic letters, digits, _ - . and single spaces, with at least one letter',
  password_required: 'Enter a password',
  password_too_short: 'The password is too short',
  password_too_long: 'The password is too long',
  password_invalid: 'The password must not be only spaces or contain control characters or broken (lone surrogate) characters',
};

/** VALIDATION_FAILED { reason, field } of a refused nickname or password (never with the value itself). */
export function credentialProblemError(p: CredentialProblem): BackendError {
  return new BackendError('VALIDATION_FAILED', CREDENTIAL_PROBLEM_MESSAGES[p.reason], { reason: p.reason, field: p.field });
}

/**
 * VALIDATION_FAILED { reason, field: 'displayName' } of a display name that breaks the nickname rules (auth.guest,
 * auth.register, profile.setDisplayName: the name is shown to other players like a nickname).
 */
export function displayNameProblemError(reason: NicknameProblem): BackendError {
  return new BackendError('VALIDATION_FAILED', CREDENTIAL_PROBLEM_MESSAGES[reason], { reason, field: 'displayName' });
}

/** CONFLICT nickname_taken of a name another account shows (or a reserved one). */
function nameTakenError(field: 'nickname' | 'displayName'): BackendError {
  return new BackendError('CONFLICT', field === 'nickname' ? 'This nickname is already taken' : 'This name is already taken', { reason: 'nickname_taken', field });
}

/** Most sign-in failure counters kept in memory (../failure-counter.ts: locked ones are kept). */
const MAX_FAILURE_KEYS = 50_000;

/** Where a password check comes from (the client address keys the per-address failure limit). */
export interface SignInMeta {
  address?: string | null;
}

/** A failure counter key of a password check and the limit it counts against. */
interface AttemptKey {
  key: string;
  max: number;
  scope: 'nickname' | 'email' | 'address';
  /** false: failures are counted on the key, but its limit does not refuse this attempt (see attemptKeys). */
  enforce?: boolean;
}

/** Addresses remembered per name whose right password they gave lately (see attemptKeys), and names remembered. */
const KNOWN_ADDRESSES_PER_NAME = 4;
const MAX_KNOWN_NAMES = 50_000;

/** Mailbox / rate-limit key of a device session (AuthState.tokenHash): stable across token refreshes. */
export function sessionKey(sessionId: string): string {
  return `as:${sessionId}`;
}

/**
 * Mutex key of a player's device sessions and of every read-modify-write of its account record (openSession, link,
 * saveAccount). The account deletion (./account-deletion.ts) holds it while it deletes the account, so none of them
 * brings a part of the account back; before that, from its first step on, no device session of the account opens
 * (beginDeletion).
 */
export function authLockKey(playerId: PlayerId): string {
  return `auth:${playerId}`;
}

/** NOT_FOUND 'account_not_found': the account no longer exists (it was deleted meanwhile). */
function accountNotFound(): BackendError {
  return new BackendError('NOT_FOUND', 'Account not found', { reason: 'account_not_found' });
}

/** Result of an auth request plus the connection binding it implies. */
export interface AuthOutcome<R extends AuthResult = AuthResult> {
  result: R;
  auth: AuthState;
}

export type SessionEndReason = 'logout' | 'revoked' | 'evicted' | 'replaced' | 'refresh_reused' | 'expired';

/** Replaced sessions an account remembers (AccountSessionsRecord.replaced; the oldest go first). */
export const REPLACED_SESSIONS_KEPT = 16;

/** Called with the sessions that ended (the core drops their connections and mailboxes). */
export type SessionsEndedListener = (playerId: PlayerId, sessionIds: readonly string[], reason: SessionEndReason) => void;

/** The username index record of an account (written in one batch with the account's credentials). */
function usernameOp(username: string, playerId: PlayerId): WriteOp {
  const value: UsernameRecord = { username, playerId };
  return { op: 'put', collection: C.usernames, id: username, value };
}

/** What the keyed hash of an auth.refresh request id is taken of (AuthSessionRecord.refreshRequest). */
function refreshRequestText(requestId: string): string {
  return `auth.refresh:${requestId}`;
}

/** How often a session's lastSeenAt is written at most. */
const TOUCH_INTERVAL_MS = 60_000;

/** Replaced refresh tokens kept per session for reuse detection (the records of older ones are deleted). */
export const ROTATED_REFRESH_TOKENS_KEPT = 8;

const UNKNOWN_DEVICE: DeviceInfo = { platform: 'unknown' };

/** Copy of the known DeviceInfo fields (the validator already refused anything else). */
function normalizeDevice(d: DeviceInfo | undefined, fallback: DeviceInfo = UNKNOWN_DEVICE): DeviceInfo {
  const src = d ?? fallback;
  const out: DeviceInfo = { platform: src.platform };
  if (src.model !== undefined) out.model = src.model;
  if (src.appVersion !== undefined) out.appVersion = src.appVersion;
  if (src.label !== undefined) out.label = src.label;
  return out;
}

interface IssuedPair {
  access: string;
  accessHash: string;
  accessExpiresAt: number;
  refresh: string;
  refreshHash: string;
  refreshExpiresAt: number;
  records: AuthTokenRecord[];
}

type WriteOp = { op: 'put'; collection: string; id: string; value: unknown } | { op: 'delete'; collection: string; id: string };

export class AuthService {
  /** Failed password checks per name (`n:<key>`) and per client address (`a:<address key>`). */
  private readonly failures: FailureCounter;
  /**
   * Results of recent rotations by the hash of the replaced refresh token (memory only; kept for the longer of the
   * reuse grace window and the refresh retry window).
   */
  private readonly recentRotations = new Map<string, { expires: number; sessionId: string; generation: number; outcome: AuthOutcome<IssuedAuthResult> }>();
  private readonly endedListeners = new Set<SessionsEndedListener>();
  /** Accounts being deleted (./account-deletion.ts) -> deletions under way: no device session of them opens. */
  private readonly deleting = new Map<PlayerId, number>();
  /** Name key (`n:<key>`, `e:<key>`) -> address keys that signed in to it with the right password lately (newest last). */
  private readonly knownAddresses = new Map<string, string[]>();
  readonly digest: TokenDigest;

  constructor(
    private readonly d: ServiceDeps,
    private readonly hasher: PasswordHasher | null,
    digest: TokenDigest = new TokenDigest(null),
  ) {
    this.digest = digest;
    this.failures = new FailureCounter(() => this.d.config.auth.loginFailureWindowMs, MAX_FAILURE_KEYS);
  }

  get credentialsEnabled(): boolean {
    return this.hasher !== null;
  }

  /** Registers a listener for ended sessions; returns the unregister function. */
  onSessionsEnded(listener: SessionsEndedListener): () => void {
    this.endedListeners.add(listener);
    return () => this.endedListeners.delete(listener);
  }

  private emitEnded(playerId: PlayerId, sessionIds: readonly string[], reason: SessionEndReason): void {
    if (sessionIds.length === 0) return;
    for (const l of [...this.endedListeners]) {
      try {
        l(playerId, sessionIds, reason);
      } catch (err) {
        this.d.logger.error('session end listener failed', { err });
      }
    }
  }

  // ------------------------------------------------------------------------------------------------------
  // Accounts

  async getAccount(playerId: PlayerId): Promise<AccountRecord | null> {
    return this.d.storage.get<AccountRecord>(C.accounts, playerId);
  }

  async requireAccount(playerId: PlayerId): Promise<AccountRecord> {
    const a = await this.getAccount(playerId);
    if (!a) throw accountNotFound();
    return a;
  }

  /**
   * Writes the account (updatedAt = now) under the player's auth lock, and only while it still exists: a write that
   * raced with the account's deletion (which holds that lock) is refused NOT_FOUND 'account_not_found' instead of
   * bringing a part of the deleted account back.
   */
  async saveAccount(a: AccountRecord): Promise<void> {
    await this.d.mutex.run(authLockKey(a.playerId), async () => {
      if (!(await this.getAccount(a.playerId))) throw accountNotFound();
      a.updatedAt = this.d.clock.now();
      await this.d.storage.put(C.accounts, a.playerId, a);
    });
  }

  async listAccounts(): Promise<AccountRecord[]> {
    return this.d.storage.list<AccountRecord>(C.accounts);
  }

  activeBan(a: AccountRecord): BanInfo | null {
    if (!a.ban) return null;
    if (a.ban.until !== null && a.ban.until <= this.d.clock.now()) return null;
    return a.ban;
  }

  assertNotBanned(a: AccountRecord): void {
    const ban = this.activeBan(a);
    if (ban) throw new BackendError('BANNED', `Account is banned: ${ban.reason}`, { reason: 'account_banned', ban });
  }

  accountInfo(a: AccountRecord): AccountInfo {
    return { playerId: a.playerId, displayName: a.displayName, username: a.username, guest: a.username === null, createdAt: a.createdAt };
  }

  /**
   * A default display name no other account got: `<prefix>-XXXX` (4 random base-36 characters; 6 after repeated
   * collisions). Issued names are recorded in C.defaultNames and never handed out again, even after a rename.
   */
  private uniqueDefaultName(): Promise<string> {
    const prefix = this.d.config.auth.defaultDisplayName;
    const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return this.d.mutex.run('default-names', async () => {
      for (let attempt = 0; attempt < 64; attempt++) {
        let suffix = '';
        for (let i = attempt < 16 ? 4 : 6; i > 0; i--) suffix += alphabet[randomInt(this.d.rng, alphabet.length)];
        const name = `${prefix}-${suffix}`;
        const key = name.toLowerCase();
        if (await this.d.storage.get(C.defaultNames, key)) continue;
        // A login of that spelling (a username account of an earlier configuration) keeps it too.
        if (await this.d.storage.get(C.usernames, key)) continue;
        await this.d.storage.put(C.defaultNames, key, { name, at: this.d.clock.now() });
        return name;
      }
      // Unreachable with a working random source (36^6 names); never loop forever on a broken one.
      return `${prefix}-${randomId(this.d.rng, '', 5).toUpperCase()}`;
    });
  }

  /**
   * A new account record, not yet stored: openSession writes it in one batch with its credentials (device or username
   * record) and its first session, so a crash never leaves an account nobody can sign in to.
   */
  private async newAccount(displayName: string | undefined): Promise<AccountRecord> {
    const now = this.d.clock.now();
    const a: AccountRecord = {
      playerId: randomId(this.d.rng, 'p_', 10),
      displayName: displayName ?? (await this.uniqueDefaultName()),
      createdAt: now,
      updatedAt: now,
      lastSeenAt: now,
      username: null,
      passwordHash: null,
      badges: [],
      ban: null,
      captures: 0,
      nextTributeAt: 0,
    };
    return a;
  }

  // ------------------------------------------------------------------------------------------------------
  // Storage helpers

  /** Several writes at once: atomically where the adapter offers `batch`, else in order. */
  private async writeAll(ops: WriteOp[]): Promise<void> {
    const storage = this.d.storage as { batch?: (ops: WriteOp[]) => Promise<void> };
    if (typeof storage.batch === 'function') {
      await storage.batch(ops);
      return;
    }
    for (const o of ops) {
      if (o.op === 'put') await this.d.storage.put(o.collection, o.id, o.value);
      else await this.d.storage.delete(o.collection, o.id);
    }
  }

  private async loadSessions(playerId: PlayerId): Promise<AccountSessionsRecord> {
    return (await this.d.storage.get<AccountSessionsRecord>(C.authSessions, playerId)) ?? { playerId, sessions: [] };
  }

  private sessionsOp(doc: AccountSessionsRecord): WriteOp {
    if (doc.replaced) {
      doc.replaced = this.liveReplaced(doc, this.d.clock.now());
      if (doc.replaced.length === 0) delete doc.replaced;
    }
    return doc.sessions.length > 0 || (doc.replaced?.length ?? 0) > 0
      ? { op: 'put', collection: C.authSessions, id: doc.playerId, value: doc }
      : { op: 'delete', collection: C.authSessions, id: doc.playerId };
  }

  private async findSession(playerId: PlayerId, sessionId: string): Promise<AuthSessionRecord | null> {
    return (await this.lookupSession(playerId, sessionId)).session;
  }

  /** A session of the account, or why it is gone ('session_replaced' while the account remembers it, else 'revoked'). */
  private async lookupSession(playerId: PlayerId, sessionId: string): Promise<{ session: AuthSessionRecord; ended: null } | { session: null; ended: AuthFailureReason }> {
    const doc = await this.d.storage.get<AccountSessionsRecord>(C.authSessions, playerId);
    const session = doc?.sessions.find((s) => s.sessionId === sessionId) ?? null;
    return session ? { session, ended: null } : { session: null, ended: this.endedReason(doc, sessionId) };
  }

  /** The refusal of a session the account no longer has: 'session_replaced' while it is remembered as replaced. */
  private endedReason(doc: AccountSessionsRecord | null, sessionId: string): AuthFailureReason {
    return doc && this.liveReplaced(doc, this.d.clock.now()).some((r) => r.sessionId === sessionId) ? 'session_replaced' : 'revoked';
  }

  /** The replaced sessions still remembered (a token of an older one has expired anyway). */
  private liveReplaced(doc: AccountSessionsRecord, now: number): ReplacedSessionRecord[] {
    return (doc.replaced ?? []).filter((r) => now - r.at < this.d.config.auth.refreshTtlMs);
  }

  /**
   * One active session per account (config.auth.singleSession): every session of `doc` except `keep` ends and is
   * remembered as replaced by it. Changes `doc` only (the caller writes it, then calls reportReplaced); returns the
   * ended ids. Does nothing with singleSession off.
   */
  private replaceOthers(doc: AccountSessionsRecord, keep: AuthSessionRecord, now: number): string[] {
    if (!this.d.config.auth.singleSession) return [];
    const ids = doc.sessions.filter((s) => s.sessionId !== keep.sessionId).map((s) => s.sessionId);
    if (ids.length === 0) return [];
    doc.sessions = doc.sessions.filter((s) => s.sessionId === keep.sessionId);
    const records = ids.map((sessionId) => ({ sessionId, at: now, by: keep.sessionId, byPlatform: keep.device.platform }));
    doc.replaced = [...this.liveReplaced(doc, now).filter((r) => !ids.includes(r.sessionId)), ...records].slice(-REPLACED_SESSIONS_KEPT);
    return ids;
  }

  /** After the write of replaceOthers: the audit log line and the session-end listeners (the core closes them: 4004). */
  private reportReplaced(playerId: PlayerId, ids: readonly string[], by: AuthSessionRecord, via: string): void {
    if (ids.length === 0) return;
    this.d.logger.warn('sessions replaced', { playerId, sessions: ids, by: by.sessionId, platform: by.device.platform, via });
    this.emitEnded(playerId, ids, 'replaced');
  }

  /** An access or refresh token record by the token (current secret first, then previous secrets). */
  private async findToken(token: string, kind: AuthTokenRecord['kind']): Promise<{ hash: string; rec: AuthTokenRecord } | null> {
    for (const hash of [this.digest.hash(token), ...this.digest.previousHashes(token)]) {
      const rec = await this.d.storage.get<AuthTokenRecord>(C.authTokens, hash);
      if (rec && rec.kind === kind) return { hash, rec };
    }
    return null;
  }

  /** A guest device by its token; a record stored under an older hash is re-keyed to the current one. */
  private async findDevice(deviceToken: string): Promise<DeviceRecord | null> {
    const primary = this.digest.hash(deviceToken);
    const dev = await this.d.storage.get<DeviceRecord>(C.devices, primary);
    if (dev) return dev;
    for (const old of this.digest.fallbackHashes(deviceToken)) {
      const legacy = await this.d.storage.get<DeviceRecord>(C.devices, old);
      if (!legacy) continue;
      const rekeyed: DeviceRecord = { ...legacy, deviceHash: primary };
      await this.writeAll([
        { op: 'put', collection: C.devices, id: primary, value: rekeyed },
        { op: 'delete', collection: C.devices, id: old },
      ]);
      return rekeyed;
    }
    return null;
  }

  private issuePair(playerId: PlayerId, sessionId: string, generation: number, now: number): IssuedPair {
    const cfg = this.d.config.auth;
    const refreshExpiresAt = now + cfg.refreshTtlMs;
    const accessExpiresAt = Math.min(now + cfg.accessTtlMs, refreshExpiresAt);
    const access = randomToken(this.d.rng, 32);
    const refresh = randomToken(this.d.rng, 32);
    const accessHash = this.digest.hash(access);
    const refreshHash = this.digest.hash(refresh);
    return {
      access,
      accessHash,
      accessExpiresAt,
      refresh,
      refreshHash,
      refreshExpiresAt,
      records: [
        { hash: accessHash, kind: 'access', playerId, sessionId, generation, createdAt: now, expiresAt: accessExpiresAt },
        { hash: refreshHash, kind: 'refresh', playerId, sessionId, generation, createdAt: now, expiresAt: refreshExpiresAt },
      ],
    };
  }

  private tokenOps(pair: IssuedPair): WriteOp[] {
    return pair.records.map((r) => ({ op: 'put' as const, collection: C.authTokens, id: r.hash, value: r }));
  }

  private stateOf(playerId: PlayerId, sessionId: string, accessExpiresAt: number): AuthState {
    return { playerId, tokenHash: sessionKey(sessionId), expiresAt: accessExpiresAt, sessionId };
  }

  private result(
    a: AccountRecord,
    sessionId: string,
    access: { token: string; expiresAt: number },
    isNew: boolean,
    refresh?: { token: string; expiresAt: number },
    deviceToken?: string,
  ): AuthResult {
    const r: AuthResult = {
      playerId: a.playerId,
      displayName: a.displayName,
      accessToken: access.token,
      accessExpiresAt: access.expiresAt,
      sessionId,
      account: this.accountInfo(a),
      serverTime: this.d.clock.now(),
      isNew,
      username: a.username,
      token: access.token,
      expiresAt: access.expiresAt,
    };
    if (refresh) {
      r.refreshToken = refresh.token;
      r.refreshExpiresAt = refresh.expiresAt;
    }
    if (deviceToken) r.deviceToken = deviceToken;
    return r;
  }

  private issuedResult(a: AccountRecord, sessionId: string, pair: IssuedPair, isNew: boolean, deviceToken?: string): IssuedAuthResult {
    return this.result(
      a,
      sessionId,
      { token: pair.access, expiresAt: pair.accessExpiresAt },
      isNew,
      { token: pair.refresh, expiresAt: pair.refreshExpiresAt },
      deviceToken,
    ) as IssuedAuthResult;
  }

  /** Least recently used first (ties: the older session first). */
  private static lruOrder(a: AuthSessionRecord, b: AuthSessionRecord): number {
    return a.lastSeenAt - b.lastSeenAt || a.createdAt - b.createdAt;
  }

  /**
   * Opens a new device session for the account: a token pair, the session record, and the end of every other session
   * of the account (config.auth.singleSession: 'replaced'), else of the least recently used sessions beyond
   * config.auth.maxSessionsPerAccount ('evicted').
   */
  private openSession(
    a: AccountRecord,
    method: AuthSessionRecord['method'],
    device: DeviceInfo | undefined,
    deviceHash: string | null,
    extra: {
      isNew: boolean;
      deviceToken?: string;
      /** Written in the same batch as the session (credentials: a device record, a username record). */
      writes?: WriteOp[];
      /** Changes to the stored account (read again under the account's auth mutex), written in the same batch. */
      account?: (a: AccountRecord) => void;
    },
  ): Promise<AuthOutcome<IssuedAuthResult>> {
    return this.d.mutex.run(authLockKey(a.playerId), async () => {
      const now = this.d.clock.now();
      const doc = await this.loadSessions(a.playerId);
      const expired = doc.sessions.filter((s) => s.refreshExpiresAt <= now).map((s) => s.sessionId);
      doc.sessions = doc.sessions.filter((s) => s.refreshExpiresAt > now);
      const sessionId = randomId(this.d.rng, 'as_', 10);
      const pair = this.issuePair(a.playerId, sessionId, 0, now);
      const session: AuthSessionRecord = {
        sessionId,
        device: normalizeDevice(device),
        createdAt: now,
        lastSeenAt: now,
        method,
        deviceHash,
        generation: 0,
        refreshHash: pair.refreshHash,
        refreshExpiresAt: pair.refreshExpiresAt,
        previousRefreshHash: null,
        rotatedAt: 0,
      };
      const evicted: string[] = [];
      let replaced: string[] = [];
      if (this.d.config.auth.singleSession) {
        doc.sessions.push(session);
        replaced = this.replaceOthers(doc, session, now);
      } else {
        const max = Math.max(1, this.d.config.auth.maxSessionsPerAccount);
        const others = [...doc.sessions].sort(AuthService.lruOrder);
        while (others.length >= max) evicted.push(others.shift()!.sessionId);
        doc.sessions = [...doc.sessions.filter((s) => !evicted.includes(s.sessionId)), session];
      }
      // The account as stored now (other services change it too), with the sign-in time. An existing account deleted
      // meanwhile (the deletion holds this lock while it deletes) stays deleted, and one being deleted gets no session
      // (it would outlive the deletion's first step, which ended every session: beginDeletion).
      if (this.deleting.has(a.playerId)) throw accountNotFound();
      const stored = await this.getAccount(a.playerId);
      if (!stored && !extra.isNew) throw accountNotFound();
      const account = stored ?? a;
      extra.account?.(account);
      account.lastSeenAt = now;
      account.updatedAt = now;
      await this.writeAll([
        ...(extra.writes ?? []),
        ...this.tokenOps(pair),
        this.sessionsOp(doc),
        { op: 'put', collection: C.accounts, id: account.playerId, value: account },
      ]);
      this.emitEnded(a.playerId, expired, 'expired');
      if (evicted.length > 0) {
        this.d.logger.info('sessions evicted by the session limit', { playerId: a.playerId, sessions: evicted.length });
        this.emitEnded(a.playerId, evicted, 'evicted');
      }
      this.reportReplaced(a.playerId, replaced, session, method);
      return { result: this.issuedResult(account, sessionId, pair, extra.isNew, extra.deviceToken), auth: this.stateOf(a.playerId, sessionId, pair.accessExpiresAt) };
    });
  }

  // ------------------------------------------------------------------------------------------------------
  // Sign-in

  /** Guest sign-in: known device token -> its account; otherwise a new account and a new device token. */
  async guest(payload: { deviceToken?: string; displayName?: string; device?: DeviceInfo }): Promise<AuthOutcome<IssuedAuthResult>> {
    const now = this.d.clock.now();
    if (payload.deviceToken) {
      const dev = await this.findDevice(payload.deviceToken);
      if (dev) {
        const a = await this.getAccount(dev.playerId);
        if (a) {
          this.assertNotBanned(a);
          dev.lastUsedAt = now;
          return this.openSession(a, 'guest', payload.device, dev.deviceHash, {
            isNew: false,
            writes: [{ op: 'put', collection: C.devices, id: dev.deviceHash, value: dev }],
          });
        }
      }
    }
    const create = async (name: { nickname: string; key: string } | null): Promise<AuthOutcome<IssuedAuthResult>> => {
      const a = await this.newAccount(name?.nickname);
      const deviceToken = randomToken(this.d.rng, 32);
      const dev: DeviceRecord = { deviceHash: this.digest.hash(deviceToken), playerId: a.playerId, createdAt: now, lastUsedAt: now };
      const writes: WriteOp[] = [{ op: 'put', collection: C.devices, id: dev.deviceHash, value: dev }];
      if (name) writes.push(this.displayNameOp(name, a.playerId));
      const out = await this.openSession(a, 'guest', payload.device, dev.deviceHash, { isNew: true, deviceToken, writes });
      this.d.logger.info('account created', { playerId: a.playerId });
      return out;
    };
    if (payload.displayName === undefined) return create(null);
    // A chosen name is shown to other players like a nickname: the same rules and the one namespace.
    const name = checkNickname(payload.displayName);
    if (!name.ok) throw displayNameProblemError(name.reason);
    return this.withNameLocks([name.key], async () => {
      if (await this.nameTaken(name.key, null)) throw nameTakenError('displayName');
      return create(name);
    });
  }

  // ------------------------------------------------------------------------------------------------------
  // The one namespace of names (nicknames, logins, display names, guest default names)

  /**
   * Runs `fn` under the locks of the names (their skeletons, so lookalike names share a lock; taken in a fixed order,
   * so two claims of several names never wait for each other). Never nested for the same name.
   */
  withNameLocks<T>(keys: readonly string[], fn: () => Promise<T>): Promise<T> {
    const locks = [...new Set(keys.map(nicknameSkeleton))].sort();
    const run = (i: number): Promise<T> => (i >= locks.length ? fn() : this.d.mutex.run(`username:${locks[i]}`, () => run(i + 1)));
    return run(0);
  }

  /** Who holds a name key: an account (its id), a reserved name nobody may take (playerId null), or nobody (null). */
  private async holderOf(key: string): Promise<{ playerId: PlayerId | null } | null> {
    if (isGuestDefaultName(key, this.d.config.auth.defaultDisplayName)) return { playerId: null };
    const u = await this.d.storage.get<UsernameRecord>(C.usernames, key);
    if (u) return { playerId: u.playerId };
    const shown = await this.d.storage.get<DisplayNameRecord>(C.displayNames, key);
    if (shown) return { playerId: shown.playerId };
    if (await this.d.storage.get(C.defaultNames, key)) return { playerId: null };
    return null;
  }

  /**
   * Whether a name key or its lookalike is taken for `self` (null: a new account): another account has it as its
   * nickname, login or display name, or it is a reserved guest default name. Call under withNameLocks([key]).
   */
  async nameTaken(key: string, self: PlayerId | null): Promise<boolean> {
    for (const k of [key, ...nicknameLookalikes(key)]) {
      const holder = await this.holderOf(k);
      if (holder && (holder.playerId === null || holder.playerId !== self)) return true;
    }
    return false;
  }

  private displayNameOp(name: { nickname: string; key: string }, playerId: PlayerId): WriteOp {
    const value: DisplayNameRecord = { name: name.nickname, playerId, at: this.d.clock.now() };
    return { op: 'put', collection: C.displayNames, id: name.key, value };
  }

  /**
   * profile.setDisplayName: the new name of an account, checked by the nickname rules and against the one namespace
   * under the name's lock. A nickname account shows its nickname: only another spelling of the same key is accepted
   * (its case), else FORBIDDEN 'nickname_locked'. Another account's name gets a record in C.displayNames and its
   * previous record is dropped. `store` writes the account (with the returned name, `nickname` as well for a nickname
   * account) and the given writes in ONE batch, still under the lock.
   */
  async changeDisplayName(
    playerId: PlayerId,
    raw: string,
    store: (name: string, nicknameAccount: boolean, writes: WriteOp[]) => Promise<void>,
  ): Promise<string> {
    const name = checkNickname(raw);
    if (!name.ok) throw displayNameProblemError(name.reason);
    return this.withNameLocks([name.key], async () => {
      const a = await this.requireAccount(playerId);
      if (a.nickname) {
        if (name.key !== a.username) {
          throw new BackendError('FORBIDDEN', 'The name of this account is its nickname', { reason: 'nickname_locked', field: 'displayName' });
        }
        await store(name.nickname, true, []);
        return name.nickname;
      }
      if (await this.nameTaken(name.key, playerId)) throw nameTakenError('displayName');
      const writes: WriteOp[] = [];
      // The account's login needs no record of its own (the usernames index holds it).
      if (name.key !== a.username) writes.push(this.displayNameOp(name, playerId));
      const oldKey = nicknameKey(a.displayName);
      if (oldKey !== name.key) {
        const old = await this.d.storage.get<DisplayNameRecord>(C.displayNames, oldKey);
        if (old && old.playerId === playerId) writes.push({ op: 'delete', collection: C.displayNames, id: oldKey });
      }
      await store(name.nickname, false, writes);
      return name.nickname;
    });
  }

  private requireHasher(): PasswordHasher {
    // The router refuses these requests first (the 'credentials' feature is off without a hasher); same answer here.
    if (!this.hasher) throw new BackendError('FORBIDDEN', 'Username/password accounts are not enabled on this backend', { reason: 'feature_disabled', feature: 'credentials' });
    return this.hasher;
  }

  async register(payload: { username: string; password: string; displayName?: string; device?: DeviceInfo }): Promise<AuthOutcome<IssuedAuthResult>> {
    const hasher = this.requireHasher();
    const username = payload.username.toLowerCase();
    let shown: { nickname: string; key: string } | null = null;
    if (payload.displayName !== undefined) {
      const name = checkNickname(payload.displayName);
      if (!name.ok) throw displayNameProblemError(name.reason);
      shown = { nickname: name.nickname, key: name.key };
    }
    // The account, its username record and its first session are one batch (a crash never leaves an account whose
    // username is not indexed: nobody could log in to it and another account could take the name).
    return this.withNameLocks(shown ? [username, shown.key] : [username], async () => {
      // The login is a name of the one namespace too (nicknames and display names included).
      if (await this.nameTaken(username, null)) throw new BackendError('CONFLICT', 'Username is already taken', { reason: 'username_taken' });
      if (shown && shown.key !== username && (await this.nameTaken(shown.key, null))) throw nameTakenError('displayName');
      const passwordHash = await hasher.hash(payload.password);
      const acc = await this.newAccount(shown ? shown.nickname : payload.username);
      acc.username = username;
      acc.passwordHash = passwordHash;
      const writes: WriteOp[] = [usernameOp(username, acc.playerId)];
      if (shown && shown.key !== username) writes.push(this.displayNameOp(shown, acc.playerId));
      const out = await this.openSession(acc, 'register', payload.device, null, { isNew: true, writes });
      this.d.logger.info('account created', { playerId: acc.playerId });
      return out;
    });
  }

  /** The failure counters a password check of the name (a nickname or login key) from `meta` counts against. */
  /**
   * The failure keys of a password check of `name`: the name's, and the client address's. An address that signed in to
   * the name with its right password lately (knownAddresses) is not refused by the name's lock (somebody else's wrong
   * passwords: the name is public) but by a key of the name and that address with the same limit; its failures count on
   * the name too, so they still lock the name for everybody else.
   */
  private attemptKeys(name: string, meta: SignInMeta | undefined, scope: 'nickname' | 'email' = 'nickname'): AttemptKey[] {
    const A = this.d.config.auth;
    const nameKey = `${scope === 'email' ? 'e' : 'n'}:${name}`;
    const address = meta?.address ? addressLimitKey(meta.address) : null;
    const known = address !== null && (this.knownAddresses.get(nameKey)?.includes(address) ?? false);
    const keys: AttemptKey[] = [{ key: nameKey, max: A.maxLoginFailures, scope, enforce: !known }];
    if (known) keys.push({ key: `${nameKey}|${address}`, max: A.maxLoginFailures, scope });
    if (address !== null && A.maxLoginFailuresPerAddress > 0) keys.push({ key: `a:${address}`, max: A.maxLoginFailuresPerAddress, scope: 'address' });
    return keys;
  }

  /** The address gave the name's right password: it is known to the name from now on (see attemptKeys). */
  private rememberAddress(nameKey: string, address: string): void {
    const list = (this.knownAddresses.get(nameKey) ?? []).filter((x) => x !== address);
    list.push(address);
    if (list.length > KNOWN_ADDRESSES_PER_NAME) list.shift();
    this.knownAddresses.delete(nameKey);
    this.knownAddresses.set(nameKey, list);
    if (this.knownAddresses.size > MAX_KNOWN_NAMES) this.knownAddresses.delete(this.knownAddresses.keys().next().value as string);
  }

  /**
   * Runs one password check under the failure limits of `keys` (see the module comment): refused with RATE_LIMITED
   * `reason` (retryAfterMs, scope) while a limit is reached by failures or by the checks under way; `check` resolves
   * true (right password: the name's failures are forgotten) or false (a failure of every key). Nothing awaits
   * between the limit check and the reservation.
   */
  private async limitedCheck(keys: readonly AttemptKey[], reason: 'too_many_attempts' | 'too_many_login_failures', check: () => Promise<boolean>): Promise<boolean> {
    const now = this.d.clock.now();
    for (const k of keys) {
      if (k.enforce === false) continue;
      const retryAfterMs = this.failures.retryAfter(k.key, k.max, now);
      if (retryAfterMs === null) continue;
      const message =
        reason === 'too_many_login_failures'
          ? 'Too many failed login attempts, try again later'
          : k.scope === 'address'
            ? 'Too many failed sign-ins from this address, try again later'
            : k.scope === 'email'
              ? 'Too many failed sign-ins with this e-mail address, try again later'
              : 'Too many failed sign-ins with this nickname, try again later';
      throw new BackendError('RATE_LIMITED', message, { reason, retryAfterMs, scope: k.scope });
    }
    for (const k of keys) this.failures.reserve(k.key);
    try {
      const ok = await check();
      const at = this.d.clock.now();
      if (ok) {
        // (a known address's success leaves the name's lock by others' failures as it is: it was never held by it)
        for (const k of keys) if (k.scope !== 'address' && k.enforce !== false) this.failures.clear(k.key);
        const address = keys.find((k) => k.scope === 'address')?.key.slice(2);
        if (address) this.rememberAddress(keys[0].key, address);
      } else for (const k of keys) this.failures.note(k.key, at, k.max);
      return ok;
    } finally {
      for (const k of keys) this.failures.release(k.key);
    }
  }

  async login(payload: { username: string; password: string; device?: DeviceInfo }, meta?: SignInMeta): Promise<AuthOutcome<IssuedAuthResult>> {
    const hasher = this.requireHasher();
    const username = payload.username.toLowerCase();
    const found: { account: AccountRecord | null } = { account: null };
    const ok = await this.limitedCheck(this.attemptKeys(username, meta), 'too_many_login_failures', async () => {
      const u = await this.d.storage.get<UsernameRecord>(C.usernames, username);
      const acc = u ? await this.getAccount(u.playerId) : null;
      found.account = acc;
      // Always run the hasher to keep timing independent of whether the user exists.
      return acc?.passwordHash ? await hasher.verify(payload.password, acc.passwordHash) : (await hasher.hash(payload.password), false);
    });
    const a = found.account;
    if (!a || !ok) throw authFailure('bad_credentials');
    this.assertNotBanned(a);
    return this.openSession(a, 'login', payload.device, null, { isNew: false });
  }

  /**
   * Whether a nickname key is taken for a new account: another account has it or its lookalike (nickname, username or
   * display name: one namespace), or it is a guest default name (handed out already, or of the `<prefix>-XXXX` form
   * that guests get).
   */
  async nicknameTaken(key: string): Promise<boolean> {
    return this.nameTaken(key, null);
  }

  /**
   * auth.signUp: a new account with a nickname and a password, signed in at once. `initialProfile` returns the writes
   * of the player's initial game state (the caller builds it); the account, its nickname index record, its first
   * device session and those writes are ONE batch, so a crash never leaves an account without its state or index.
   */
  async signUp(
    payload: { nickname: string; password: string; device?: DeviceInfo },
    initialProfile?: (account: AccountRecord) => WriteOp[],
  ): Promise<AuthOutcome<IssuedAuthResult>> {
    const hasher = this.requireHasher();
    const problem = checkSignUp(payload.nickname, payload.password);
    if (problem) throw credentialProblemError(problem);
    const nickname = normalizeNickname(payload.nickname);
    const key = nicknameKey(nickname);
    // The same lock as auth.register / auth.link of that login and every claim of a lookalike name: one namespace.
    return this.withNameLocks([key], async () => {
      if (await this.nicknameTaken(key)) throw nameTakenError('nickname');
      const passwordHash = await hasher.hash(payload.password);
      const acc = await this.newAccount(nickname);
      acc.username = key;
      acc.nickname = nickname;
      acc.passwordHash = passwordHash;
      const writes: WriteOp[] = [usernameOp(key, acc.playerId), ...(initialProfile?.(acc) ?? [])];
      const out = await this.openSession(acc, 'signup', payload.device, null, { isNew: true, writes });
      this.d.logger.info('account created', { playerId: acc.playerId, method: 'signUp' });
      return out;
    });
  }

  /**
   * auth.signIn with the nickname (any case; username accounts sign in with their username). An unknown nickname and
   * a wrong password are the same 'invalid_credentials' after about the same time (see the module comment).
   */
  async signIn(payload: { nickname: string; password: string; device?: DeviceInfo }, meta?: SignInMeta): Promise<AuthOutcome<IssuedAuthResult>> {
    const hasher = this.requireHasher();
    const problem = checkSignIn(payload.nickname, payload.password);
    if (problem) throw credentialProblemError(problem);
    const key = nicknameKey(payload.nickname);
    const found: { account: AccountRecord | null } = { account: null };
    const ok = await this.limitedCheck(this.attemptKeys(key, meta), 'too_many_attempts', async () => {
      const u = await this.d.storage.get<UsernameRecord>(C.usernames, key);
      const acc = u ? await this.getAccount(u.playerId) : null;
      found.account = acc;
      // An unknown nickname hashes the password all the same: its answer takes as long as a wrong password's.
      return acc?.passwordHash ? await hasher.verify(payload.password, acc.passwordHash) : (await hasher.hash(payload.password), false);
    });
    const a = found.account;
    if (!a || !ok) throw authFailure('invalid_credentials');
    this.assertNotBanned(a);
    return this.openSession(a, 'signin', payload.device, null, { isNew: false });
  }

  /**
   * auth.emailSignIn: the account of a confirmed address (`lookup` finds it, inside the limited check) with its
   * password. The same answers, limits and timing as auth.signIn: an unknown address and a wrong password are the same
   * 'invalid_credentials' after about one hash; failures count per address key (details.scope 'email') and per client
   * address. `key` is the address's emailKey (src/shared/email-rules.ts).
   */
  async signInByEmail(key: string, password: string, lookup: () => Promise<PlayerId | null>, device: DeviceInfo | undefined, meta?: SignInMeta): Promise<AuthOutcome<IssuedAuthResult>> {
    const hasher = this.requireHasher();
    const problem = checkSignIn('xxx', password);
    if (problem) throw credentialProblemError(problem);
    const found: { account: AccountRecord | null } = { account: null };
    const ok = await this.limitedCheck(this.attemptKeys(key, meta, 'email'), 'too_many_attempts', async () => {
      const playerId = await lookup();
      const acc = playerId ? await this.getAccount(playerId) : null;
      found.account = acc;
      return acc?.passwordHash ? await hasher.verify(password, acc.passwordHash) : (await hasher.hash(password), false);
    });
    const a = found.account;
    if (!a || !ok) throw authFailure('invalid_credentials');
    this.assertNotBanned(a);
    return this.openSession(a, 'email', device, null, { isNew: false });
  }

  /**
   * auth.passwordReset (services/email.ts checked the link): the account's new password (the sign-up rules), stored as
   * its hash; every device session of the account ends ('revoked': whoever knew the old password is out) and the
   * sign-in failures of its login and of `emailKey` are forgotten. Returns the ended session ids.
   */
  async resetPassword(playerId: PlayerId, password: string, emailKey: string | null): Promise<string[]> {
    const hasher = this.requireHasher();
    const problem = checkPassword(password);
    if (!problem.ok) throw credentialProblemError({ field: 'password', reason: problem.reason });
    const passwordHash = await hasher.hash(password);
    await this.d.mutex.run(authLockKey(playerId), async () => {
      const a = await this.requireAccount(playerId);
      a.passwordHash = passwordHash;
      a.updatedAt = this.d.clock.now();
      // The guest device tokens of the account go too: a device that kept one would sign in again without a password.
      await this.writeAll([{ op: 'put', collection: C.accounts, id: playerId, value: a }, ...(await this.deviceDeletes(playerId))]);
      if (a.username) this.failures.clear(`n:${a.username}`);
    });
    if (emailKey) this.failures.clear(`e:${emailKey}`);
    return this.endSessions(playerId, 'all', 'revoked');
  }

  /**
   * A sign-in the caller has already authenticated by other means (auth.playGames: Google vouched for the Play Games
   * player, services/play-games.ts found the linked account): a new device session, as a sign-in opens one.
   */
  async signInVerified(playerId: PlayerId, method: 'play_games', device?: DeviceInfo): Promise<AuthOutcome<IssuedAuthResult>> {
    const a = await this.getAccount(playerId);
    if (!a) throw new BackendError('NOT_FOUND', 'No account is linked to this Play Games player', { reason: 'play_games_not_linked' });
    this.assertNotBanned(a);
    return this.openSession(a, method, device, null, { isNew: false });
  }

  /** auth.session: the device session of a bound connection (its access token's expiry included). */
  async currentSession(auth: AuthState): Promise<CurrentSessionInfo> {
    const found = auth.sessionId ? await this.lookupSession(auth.playerId, auth.sessionId) : null;
    const s = found?.session ?? null;
    if (!s) throw authFailure(found?.ended ?? 'revoked');
    return {
      sessionId: s.sessionId,
      device: normalizeDevice(s.device),
      createdAt: s.createdAt,
      lastSeenAt: s.lastSeenAt,
      accessExpiresAt: auth.expiresAt,
      refreshExpiresAt: s.refreshExpiresAt,
    };
  }

  // ------------------------------------------------------------------------------------------------------
  // Tokens

  /**
   * Resolves a bearer access token (HTTP `Authorization`, auth.resume): the connection binding, or why it is refused
   * ('invalid_token', 'token_expired', 'revoked', 'session_replaced').
   */
  async authenticate(token: string): Promise<{ auth: AuthState } | { reason: AuthFailureReason }> {
    const found = await this.findToken(token, 'access');
    if (!found) return { reason: 'invalid_token' };
    const { rec } = found;
    const now = this.d.clock.now();
    const { session, ended } = await this.lookupSession(rec.playerId, rec.sessionId);
    if (!session) return { reason: ended };
    // An access token never outlives its session's refresh token: an ended session reads as an expired token here
    // (auth.refresh then answers 'refresh_expired').
    if (rec.expiresAt <= now || session.refreshExpiresAt <= now) return { reason: 'token_expired' };
    return { auth: this.stateOf(rec.playerId, rec.sessionId, rec.expiresAt) };
  }

  /**
   * Per-request check of a bound connection: the access token has not expired and its session still exists. Writes the
   * session's lastSeenAt at most once a minute. Returns the refusal reason, or null.
   */
  async validate(auth: AuthState): Promise<AuthFailureReason | null> {
    const now = this.d.clock.now();
    if (auth.expiresAt <= now) return 'token_expired';
    if (!auth.sessionId) return null;
    const { session, ended } = await this.lookupSession(auth.playerId, auth.sessionId);
    if (!session) return ended;
    if (session.refreshExpiresAt <= now) return 'token_expired';
    if (now - session.lastSeenAt >= TOUCH_INTERVAL_MS) await this.touch(auth.playerId, auth.sessionId, undefined);
    return null;
  }

  /** Updates a session's lastSeenAt (and its device info when given). */
  private touch(playerId: PlayerId, sessionId: string, device: DeviceInfo | undefined): Promise<void> {
    return this.d.mutex.run(authLockKey(playerId), async () => {
      const doc = await this.loadSessions(playerId);
      const s = doc.sessions.find((x) => x.sessionId === sessionId);
      if (!s) return;
      s.lastSeenAt = this.d.clock.now();
      if (device) s.device = normalizeDevice(device);
      await this.d.storage.put(C.authSessions, playerId, doc);
    });
  }

  /**
   * auth.resume: the session becomes the account's one active session (config.auth.singleSession: its other sessions
   * are replaced) and its lastSeenAt (and device info) is written. A session that ended meanwhile is refused.
   */
  private claim(playerId: PlayerId, sessionId: string, device: DeviceInfo | undefined): Promise<void> {
    return this.d.mutex.run(authLockKey(playerId), async () => {
      const now = this.d.clock.now();
      const doc = await this.loadSessions(playerId);
      const s = doc.sessions.find((x) => x.sessionId === sessionId);
      if (!s) throw authFailure(this.endedReason(doc, sessionId));
      s.lastSeenAt = now;
      if (device) s.device = normalizeDevice(device);
      const replaced = this.replaceOthers(doc, s, now);
      await this.writeAll([this.sessionsOp(doc)]);
      this.reportReplaced(playerId, replaced, s, 'resume');
    });
  }

  /**
   * Binds a connection to an existing session by its access token (a reconnected WebSocket). A session token of the
   * protocol 1 backend is converted into a new device session once (the result then carries a refresh token).
   */
  async resume(token: string, device?: DeviceInfo): Promise<AuthOutcome> {
    const r = await this.authenticate(token);
    if ('reason' in r) {
      if (r.reason === 'invalid_token') {
        const converted = await this.convertLegacyToken(token, device);
        if (converted) return converted;
      }
      throw authFailure(r.reason);
    }
    const a = await this.getAccount(r.auth.playerId);
    if (!a) throw authFailure('revoked');
    this.assertNotBanned(a);
    await this.claim(a.playerId, r.auth.sessionId!, device);
    return { result: this.result(a, r.auth.sessionId!, { token, expiresAt: r.auth.expiresAt }, false), auth: r.auth };
  }

  private async convertLegacyToken(token: string, device: DeviceInfo | undefined): Promise<AuthOutcome<IssuedAuthResult> | null> {
    const legacyHash = sha256Hex(token);
    const rec = await this.d.storage.get<TokenRecord>(C.tokens, legacyHash);
    if (!rec) return null;
    const a = await this.getAccount(rec.playerId);
    if (!a) return null;
    if (rec.expiresAt <= this.d.clock.now()) {
      await this.d.storage.delete(C.tokens, legacyHash);
      throw authFailure('token_expired');
    }
    this.assertNotBanned(a);
    const out = await this.openSession(a, 'resume', device, null, { isNew: false, writes: [{ op: 'delete', collection: C.tokens, id: legacyHash }] });
    this.d.logger.info('protocol 1 session converted', { playerId: a.playerId });
    return out;
  }

  /**
   * A new token pair for the session of `refreshToken`, which is rotated. `requestId` (the envelope id) marks the
   * rotation: a retry under the same id with the previous refresh token within the retry window gets a valid pair
   * (see the module comment). The directly previous refresh token under another id within the grace window gets the
   * same new pair again; any other rotated one revokes the session ('refresh_reused').
   */
  async refresh(refreshToken: string, device?: DeviceInfo, requestId?: string): Promise<AuthOutcome<IssuedAuthResult>> {
    const found = await this.findToken(refreshToken, 'refresh');
    if (!found) throw authFailure('invalid_token');
    const { hash, rec } = found;
    return this.d.mutex.run(authLockKey(rec.playerId), async () => {
      const now = this.d.clock.now();
      const doc = await this.loadSessions(rec.playerId);
      const s = doc.sessions.find((x) => x.sessionId === rec.sessionId);
      if (rec.expiresAt <= now || (s && s.refreshHash === hash && s.refreshExpiresAt <= now)) {
        // Past its lifetime: the session ended with it (the sweep drops the records).
        if (s && s.refreshHash === hash) {
          doc.sessions = doc.sessions.filter((x) => x.sessionId !== s.sessionId);
          await this.writeAll([this.sessionsOp(doc)]);
          this.emitEnded(rec.playerId, [s.sessionId], 'expired');
        }
        throw authFailure('refresh_expired');
      }
      if (!s) throw authFailure(this.endedReason(doc, rec.sessionId));
      if (hash !== s.refreshHash) {
        if (hash === s.previousRefreshHash) {
          const recent = this.recentRotations.get(hash);
          const remembered = recent && recent.expires > now && recent.sessionId === s.sessionId && recent.generation === s.generation ? recent.outcome : null;
          // The retry of the refresh that made the last rotation: its reply was lost.
          if (this.isRefreshRetry(s, requestId, now)) return remembered ?? (await this.reissue(rec.playerId, doc, s, hash, device, now));
          const grace = this.d.config.auth.refreshReuseGraceMs;
          if (grace > 0 && now - s.rotatedAt <= grace) {
            if (remembered) return remembered;
            throw authFailure('refresh_rotated');
          }
        }
        doc.sessions = doc.sessions.filter((x) => x.sessionId !== s.sessionId);
        await this.writeAll([this.sessionsOp(doc)]);
        this.d.logger.warn('refresh token reuse detected: session revoked', { playerId: rec.playerId, sessionId: s.sessionId, generation: rec.generation });
        this.emitEnded(rec.playerId, [s.sessionId], 'refresh_reused');
        throw authFailure('refresh_reused');
      }
      const a = await this.getAccount(rec.playerId);
      if (!a) throw authFailure('revoked');
      this.assertNotBanned(a);
      const pair = this.issuePair(a.playerId, s.sessionId, s.generation + 1, now);
      const dropped = this.rotate(s, pair, now);
      if (requestId) s.refreshRequest = { idHash: this.digest.hash(refreshRequestText(requestId)), accessHash: pair.accessHash };
      if (device) s.device = normalizeDevice(device);
      const replaced = this.replaceOthers(doc, s, now);
      await this.writeAll([...this.tokenOps(pair), this.sessionsOp(doc), ...dropped]);
      this.reportReplaced(a.playerId, replaced, s, 'refresh');
      const outcome = { result: this.issuedResult(a, s.sessionId, pair, false), auth: this.stateOf(a.playerId, s.sessionId, pair.accessExpiresAt) };
      this.rememberRotation(hash, s, outcome, now);
      return outcome;
    });
  }

  /** Whether `requestId` is the id of the refresh that made the session's last rotation, within the retry window. */
  private isRefreshRetry(s: AuthSessionRecord, requestId: string | undefined, now: number): boolean {
    const window = this.d.config.auth.refreshRetryWindowMs;
    const marked = s.refreshRequest;
    if (!requestId || !marked || window <= 0 || now - s.rotatedAt > window) return false;
    const text = refreshRequestText(requestId);
    return [this.digest.hash(text), ...this.digest.previousHashes(text)].includes(marked.idHash);
  }

  /**
   * The retry of a refresh whose pair this process does not remember (it restarted): a new pair of the same generation
   * replaces the pair of the lost reply, whose tokens are deleted (they read as unknown from now on, not as a reuse).
   * Runs under the session's auth mutex.
   */
  private async reissue(
    playerId: PlayerId,
    doc: AccountSessionsRecord,
    s: AuthSessionRecord,
    previousHash: string,
    device: DeviceInfo | undefined,
    now: number,
  ): Promise<AuthOutcome<IssuedAuthResult>> {
    const a = await this.getAccount(playerId);
    if (!a) throw authFailure('revoked');
    this.assertNotBanned(a);
    const marked = s.refreshRequest!;
    const pair = this.issuePair(playerId, s.sessionId, s.generation, now);
    const lost: WriteOp[] = [s.refreshHash, marked.accessHash].map((id) => ({ op: 'delete', collection: C.authTokens, id }));
    s.refreshHash = pair.refreshHash;
    s.refreshExpiresAt = pair.refreshExpiresAt;
    s.refreshRequest = { idHash: marked.idHash, accessHash: pair.accessHash };
    s.lastSeenAt = now;
    if (device) s.device = normalizeDevice(device);
    await this.writeAll([...this.tokenOps(pair), this.sessionsOp(doc), ...lost]);
    this.d.logger.info('refresh retried after a lost reply: pair re-issued', { playerId, sessionId: s.sessionId, generation: s.generation });
    const outcome = { result: this.issuedResult(a, s.sessionId, pair, false), auth: this.stateOf(playerId, s.sessionId, pair.accessExpiresAt) };
    this.rememberRotation(previousHash, s, outcome, now);
    return outcome;
  }

  /**
   * Makes `pair` the session's current tokens. The replaced refresh token stays known for reuse detection; the records
   * of replaced tokens beyond ROTATED_REFRESH_TOKENS_KEPT are deleted (returned as delete ops).
   */
  private rotate(s: AuthSessionRecord, pair: IssuedPair, now: number): WriteOp[] {
    const rotated = [...(s.rotatedHashes ?? []), s.refreshHash];
    const dropped = rotated.splice(0, Math.max(0, rotated.length - ROTATED_REFRESH_TOKENS_KEPT));
    s.rotatedHashes = rotated;
    s.generation += 1;
    s.previousRefreshHash = s.refreshHash;
    s.rotatedAt = now;
    s.refreshHash = pair.refreshHash;
    s.refreshExpiresAt = pair.refreshExpiresAt;
    s.lastSeenAt = now;
    s.refreshRequest = null;
    return dropped.map((id) => ({ op: 'delete' as const, collection: C.authTokens, id }));
  }

  private rememberRotation(oldHash: string, s: AuthSessionRecord, outcome: AuthOutcome<IssuedAuthResult>, now: number): void {
    const keep = Math.max(this.d.config.auth.refreshReuseGraceMs, this.d.config.auth.refreshRetryWindowMs);
    for (const [k, v] of this.recentRotations) if (v.expires <= now) this.recentRotations.delete(k);
    if (keep <= 0) return;
    this.recentRotations.set(oldHash, { expires: now + keep, sessionId: s.sessionId, generation: s.generation, outcome });
    if (this.recentRotations.size > 10_000) this.recentRotations.delete(this.recentRotations.keys().next().value!);
  }

  /**
   * Adds username/password credentials to a guest account. The calling session's tokens are rotated (a privilege
   * change), so the result carries a new pair.
   */
  async link(playerId: PlayerId, sessionId: string | null, payload: { username: string; password: string; device?: DeviceInfo }): Promise<AuthOutcome<IssuedAuthResult>> {
    const hasher = this.requireHasher();
    const username = payload.username.toLowerCase();
    // The credentials, the username record and the rotated session are one batch (see register).
    const out = await this.withNameLocks([username], async () => {
      const current = await this.requireAccount(playerId);
      if (current.username) throw new BackendError('INVALID_STATE', 'Account already has credentials', { reason: 'already_linked' });
      // The account's own display name may become its login; any other holder's may not.
      if (await this.nameTaken(username, playerId)) throw new BackendError('CONFLICT', 'Username is already taken', { reason: 'username_taken' });
      const passwordHash = await hasher.hash(payload.password);
      // Applied to the account as stored under the auth mutex (a concurrent link may have won meanwhile).
      const addCredentials = (acc: AccountRecord) => {
        if (acc.username) throw new BackendError('INVALID_STATE', 'Account already has credentials', { reason: 'already_linked' });
        acc.username = username;
        acc.passwordHash = passwordHash;
      };
      if (!sessionId) return this.openSession(current, 'login', payload.device, null, { isNew: false, writes: [usernameOp(username, playerId)], account: addCredentials });
      return this.d.mutex.run(authLockKey(playerId), async () => {
        const now = this.d.clock.now();
        const doc = await this.loadSessions(playerId);
        const s = doc.sessions.find((x) => x.sessionId === sessionId);
        if (!s) throw authFailure(this.endedReason(doc, sessionId));
        const acc = await this.getAccount(playerId);
        if (!acc) throw accountNotFound(); // deleted meanwhile
        addCredentials(acc);
        acc.updatedAt = now;
        const pair = this.issuePair(playerId, s.sessionId, s.generation + 1, now);
        const dropped = this.rotate(s, pair, now);
        if (payload.device) s.device = normalizeDevice(payload.device);
        await this.writeAll([
          { op: 'put', collection: C.accounts, id: playerId, value: acc },
          usernameOp(username, playerId),
          ...this.tokenOps(pair),
          this.sessionsOp(doc),
          ...dropped,
        ]);
        return { result: this.issuedResult(acc, s.sessionId, pair, false), auth: this.stateOf(playerId, s.sessionId, pair.accessExpiresAt) };
      });
    });
    this.d.logger.info('credentials linked', { playerId });
    return out;
  }

  // ------------------------------------------------------------------------------------------------------
  // Sessions

  /** The account's live device sessions, most recently used first. */
  async listSessions(playerId: PlayerId, currentSessionId: string | null): Promise<AuthSessionInfo[]> {
    const now = this.d.clock.now();
    const doc = await this.loadSessions(playerId);
    return doc.sessions
      .filter((s) => s.refreshExpiresAt > now)
      .sort((a, b) => -AuthService.lruOrder(a, b))
      .map((s) => ({ sessionId: s.sessionId, device: normalizeDevice(s.device), createdAt: s.createdAt, lastSeenAt: s.lastSeenAt, current: s.sessionId === currentSessionId }));
  }

  /**
   * Ends sessions of the account: the given ids (an id the account does not have is NOT_FOUND, nothing is ended) or
   * every session ('all'). Returns the ended session ids; the session-end listeners run for them.
   */
  endSessions(playerId: PlayerId, which: readonly string[] | 'all', reason: SessionEndReason): Promise<string[]> {
    return this.d.mutex.run(authLockKey(playerId), async () => {
      const doc = await this.loadSessions(playerId);
      const ids = which === 'all' ? doc.sessions.map((s) => s.sessionId) : [...new Set(which)];
      for (const id of ids) if (!doc.sessions.some((s) => s.sessionId === id)) throw new BackendError('NOT_FOUND', 'Session not found', { reason: 'session_not_found' });
      if (ids.length === 0) return [];
      doc.sessions = doc.sessions.filter((s) => !ids.includes(s.sessionId));
      await this.writeAll([this.sessionsOp(doc)]);
      this.d.logger.info('sessions ended', { playerId, sessions: ids.length, reason });
      this.emitEnded(playerId, ids, reason);
      return ids;
    });
  }

  // ------------------------------------------------------------------------------------------------------
  // Operator API

  /**
   * Operator API (console rename): gives the account the name `raw` under the same rules and in the same one namespace
   * as auth.signUp (src/shared/auth-rules.ts; a lookalike of another account's name is taken too). A nickname
   * account's nickname is its login, so its index record moves with it (the player signs in with the new name); other
   * accounts change their display name (C.displayNames). `store(name, nicknameAccount, writes, username)` writes the
   * account with the given index writes in ONE batch, under the name locks. Returns the new name.
   */
  async renameByOperator(
    playerId: PlayerId,
    raw: string,
    store: (name: string, nicknameAccount: boolean, writes: WriteOp[], username: string | null) => Promise<void>,
  ): Promise<string> {
    const name = checkNickname(raw);
    if (!name.ok) throw credentialProblemError({ field: 'nickname', reason: name.reason });
    const before = await this.requireAccount(playerId);
    const oldKey = before.nickname ? before.username! : nicknameKey(before.displayName);
    return this.withNameLocks([name.key, oldKey], async () => {
      const a = await this.requireAccount(playerId);
      if (await this.nameTaken(name.key, playerId)) throw nameTakenError('nickname');
      const writes: WriteOp[] = [];
      if (a.nickname) {
        if (a.username && a.username !== name.key) {
          const old = await this.d.storage.get<UsernameRecord>(C.usernames, a.username);
          if (old && old.playerId === playerId) writes.push({ op: 'delete', collection: C.usernames, id: a.username });
          writes.push(usernameOp(name.key, playerId));
        }
        await store(name.nickname, true, writes, name.key);
        return name.nickname;
      }
      if (name.key !== a.username) writes.push(this.displayNameOp(name, playerId));
      const shownKey = nicknameKey(a.displayName);
      if (shownKey !== name.key) {
        const old = await this.d.storage.get<DisplayNameRecord>(C.displayNames, shownKey);
        if (old && old.playerId === playerId) writes.push({ op: 'delete', collection: C.displayNames, id: shownKey });
      }
      await store(name.nickname, false, writes, a.username);
      return name.nickname;
    });
  }

  /**
   * Operator API (console): replaces the password of an account that signs in with one (a nickname or a username
   * account; a guest has none: INVALID_STATE 'no_credentials'). The password follows the sign-up rules and is stored
   * only as its hash. With `endSessions` every device session of the account ends (the old password may be known to
   * somebody else). Returns the ended session ids.
   */
  async setPasswordByOperator(playerId: PlayerId, password: string, endSessions: boolean): Promise<string[]> {
    const hasher = this.requireHasher();
    const problem = checkPassword(password);
    if (!problem.ok) throw credentialProblemError({ field: 'password', reason: problem.reason });
    const a = await this.requireAccount(playerId);
    if (!a.username) throw new BackendError('INVALID_STATE', 'This account has no password sign-in (a guest account)', { reason: 'no_credentials' });
    a.passwordHash = await hasher.hash(password);
    await this.saveAccount(a);
    this.failures.clear(`n:${a.username}`);
    this.d.logger.warn('password replaced by an operator', { playerId });
    if (!endSessions) return [];
    // Signing every device out includes the guest device tokens (they would sign in again without a password).
    await this.d.mutex.run(authLockKey(playerId), async () => this.writeAll(await this.deviceDeletes(playerId)));
    return this.endSessions(playerId, 'all', 'revoked');
  }

  /**
   * The deletes of the guest device tokens (C.devices) of an account: a device token opens a session with no password
   * (auth.guest), so whatever signs every device out with a new password (auth.passwordReset, an operator's new
   * password) revokes them too.
   */
  private async deviceDeletes(playerId: PlayerId): Promise<WriteOp[]> {
    const devices = await this.d.storage.list<DeviceRecord>(C.devices);
    return devices.filter((x) => x?.playerId === playerId).map((x) => ({ op: 'delete' as const, collection: C.devices, id: x.deviceHash }));
  }

  /** Operator API: ban or unban a player (not exposed through the protocol). */
  async setBan(playerId: PlayerId, ban: BanInfo | null): Promise<void> {
    const a = await this.requireAccount(playerId);
    a.ban = ban;
    await this.saveAccount(a);
    this.d.logger.warn(ban ? 'player banned' : 'player unbanned', { playerId, code: ban?.code });
  }

  // ------------------------------------------------------------------------------------------------------
  // Account deletion (./account-deletion.ts)

  /**
   * The first step of an account deletion: from now on no device session of the account opens (a sign-in, a guest's
   * device token, a protocol 1 token, a link without a session: NOT_FOUND 'account_not_found', as once the account is
   * gone), and every session it has ends (reason 'revoked'). Returns the ended session ids. `endDeletion` lifts it
   * again, whether the account is gone or the deletion failed before its batch (the account stays and may sign in).
   * The mark is set before the auth lock is taken: a sign-in that holds the lock now opens its session first, and
   * that session ends here.
   */
  beginDeletion(playerId: PlayerId): Promise<string[]> {
    this.deleting.set(playerId, (this.deleting.get(playerId) ?? 0) + 1);
    return this.endSessions(playerId, 'all', 'revoked');
  }

  /** The end of a deletion that began with `beginDeletion` (see there). */
  endDeletion(playerId: PlayerId): void {
    const n = (this.deleting.get(playerId) ?? 0) - 1;
    if (n > 0) this.deleting.set(playerId, n);
    else this.deleting.delete(playerId);
  }

  /**
   * account.delete: the password that confirms the deletion of an account that signs in with one (a nickname or a
   * username account; a guest has none and deletes with its session alone). The check is a sign-in's: the same failure
   * limits (per login key and per client address: RATE_LIMITED 'too_many_attempts') and the same refusal
   * (UNAUTHORIZED 'invalid_credentials'); a missing password is VALIDATION_FAILED 'password_required'. The password
   * is never logged, stored or part of an error.
   */
  async confirmDeletion(a: AccountRecord, password: string | undefined, meta?: SignInMeta): Promise<void> {
    if (a.passwordHash === null) return;
    if (password === undefined || password === '') throw credentialProblemError({ field: 'password', reason: 'password_required' });
    const hasher = this.requireHasher();
    const hash = a.passwordHash;
    const ok = await this.limitedCheck(this.attemptKeys(a.username ?? a.playerId, meta), 'too_many_attempts', () => hasher.verify(password, hash));
    if (!ok) throw authFailure('invalid_credentials');
  }

  /**
   * The writes that delete everything of the account this service keeps: the account, its login record (C.usernames),
   * its display name records (C.displayNames), its guest devices, its device sessions and every token record (access,
   * refresh, protocol 1). Only records that name this player go; the default names issued to guests (C.defaultNames)
   * stay, since they name nobody and are never handed out again. The caller holds the player's auth lock and stores
   * the writes in one batch with the rest of the deletion, then calls `forgetDeleted` with what this returned.
   */
  async deletionWrites(playerId: PlayerId): Promise<{ ops: WriteOp[]; sessionIds: string[]; loginKey: string | null; counts: Record<string, number> }> {
    const ops: WriteOp[] = [];
    const counts: Record<string, number> = {};
    const drop = (collection: string, ids: readonly string[]) => {
      for (const id of ids) ops.push({ op: 'delete', collection, id });
      if (ids.length > 0) counts[collection] = (counts[collection] ?? 0) + ids.length;
    };
    const account = await this.getAccount(playerId);
    if (account) drop(C.accounts, [playerId]);
    const doc = await this.d.storage.get<AccountSessionsRecord>(C.authSessions, playerId);
    if (doc) drop(C.authSessions, [playerId]);
    drop(C.usernames, (await this.d.storage.list<UsernameRecord>(C.usernames)).filter((u) => u?.playerId === playerId).map((u) => u.username));
    // Display name records keep their key only inside the name; the key is the nickname key of that name.
    drop(C.displayNames, (await this.d.storage.list<DisplayNameRecord>(C.displayNames)).filter((n) => n?.playerId === playerId).map((n) => nicknameKey(n.name)));
    drop(C.devices, (await this.d.storage.list<DeviceRecord>(C.devices)).filter((x) => x?.playerId === playerId).map((x) => x.deviceHash));
    drop(C.authTokens, (await this.d.storage.list<AuthTokenRecord>(C.authTokens)).filter((t) => t?.playerId === playerId).map((t) => t.hash));
    drop(C.tokens, (await this.d.storage.list<TokenRecord>(C.tokens)).filter((t) => t?.playerId === playerId).map((t) => t.tokenHash));
    return { ops, sessionIds: (doc?.sessions ?? []).map((s) => s.sessionId), loginKey: account?.username ?? null, counts };
  }

  /**
   * After the deletion's batch: the sessions that were still open end (their connections close) and this service
   * forgets the player's sign-in failures and remembered rotations.
   */
  forgetDeleted(playerId: PlayerId, done: { sessionIds: readonly string[]; loginKey: string | null }): void {
    if (done.loginKey) this.failures.clear(`n:${done.loginKey}`);
    for (const [k, v] of this.recentRotations) if (v.outcome.auth.playerId === playerId) this.recentRotations.delete(k);
    this.emitEnded(playerId, done.sessionIds, 'revoked');
  }

  /**
   * Deletes expired protocol 1 tokens, ended or expired sessions, and the token records of ended sessions or past their
   * expiry. Returns the number of deleted token records.
   */
  async sweep(now: number): Promise<number> {
    let n = 0;
    for (const t of await this.d.storage.list<TokenRecord>(C.tokens)) {
      if (t.expiresAt <= now) {
        await this.d.storage.delete(C.tokens, t.tokenHash);
        n++;
      }
    }
    const live = new Set<string>();
    for (const listed of await this.d.storage.list<AccountSessionsRecord>(C.authSessions)) {
      const ended = await this.d.mutex.run(authLockKey(listed.playerId), async () => {
        const doc = await this.loadSessions(listed.playerId);
        const gone = doc.sessions.filter((s) => s.refreshExpiresAt <= now).map((s) => s.sessionId);
        const forgotten = (doc.replaced?.length ?? 0) !== this.liveReplaced(doc, now).length;
        if (gone.length > 0 || forgotten) {
          doc.sessions = doc.sessions.filter((s) => s.refreshExpiresAt > now);
          await this.writeAll([this.sessionsOp(doc)]);
        }
        for (const s of doc.sessions) live.add(`${doc.playerId}|${s.sessionId}`);
        // The tokens of a replaced session are kept until they expire: they answer 'session_replaced', not 'unknown'.
        for (const r of this.liveReplaced(doc, now)) live.add(`${doc.playerId}|${r.sessionId}`);
        return gone;
      });
      this.emitEnded(listed.playerId, ended, 'expired');
    }
    for (const t of await this.d.storage.list<AuthTokenRecord>(C.authTokens)) {
      // A session opened after the listing above is not in `live`: look it up before dropping its tokens.
      const ended = !live.has(`${t.playerId}|${t.sessionId}`) && (await this.lookupSession(t.playerId, t.sessionId)).ended === 'revoked';
      if (t.expiresAt <= now || ended) {
        await this.d.storage.delete(C.authTokens, t.hash);
        n++;
      }
    }
    for (const [k, v] of this.recentRotations) if (v.expires <= now) this.recentRotations.delete(k);
    return n;
  }
}
